/**
 * Customer's in-flight onboarding state.
 *
 * Plan choice + questionnaire answers persist in localStorage so a customer
 * who refreshes the page or bounces through Stripe Checkout doesn't lose
 * progress. Cleared on dashboard reach.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type PlanSlug = "starter" | "growth" | "pro";
export type SiteTypeChoice = "ecommerce" | "saas" | "museum" | "lodging" | "social_casino";
export type DomainKind = "subdomain" | "byo";

export interface QuestionnaireState {
  siteName: string;
  siteType: SiteTypeChoice;
  tagline: string; // optional short slogan — extra signal for AI content gen
  description: string;
  primaryColor: string | null; // null = let AI pick
  domain: { kind: DomainKind; value: string };
  adminEmail: string;
  // Lodging only: Airbnb listing URLs captured in the questionnaire so the
  // post-provision "Import from Airbnb" step can pre-fill them.
  airbnbListingUrls?: string[];
  // Voice bundle only: how the site's support number should be provisioned.
  // Collected in the questionnaire because the number is bought during
  // provisioning, before the customer has any admin surface to ask on.
  voiceNumberType?: VoiceNumberType;
  voiceAreaCode?: string;
}

/**
 * Toll-free reads as a business line and is the default a support number
 * normally wants; local is cheaper and skips toll-free verification, needing
 * A2P 10DLC registration instead. Neither can text until its carrier
 * programme clears.
 */
export type VoiceNumberType = "toll_free" | "local";

interface WizardStore {
  plan: PlanSlug | null;
  /** Bundle add-on slugs picked at the plan step; survives the Stripe bounce. */
  addons: string[];
  questionnaire: Partial<QuestionnaireState>;
  jobId: string | null;
  /**
   * Which of the account's sites the dashboard pages are working on. Null =
   * let the server pick (the only site, for most accounts). Survives reset():
   * finishing a build clears the draft, not which site you were looking at.
   */
  currentSiteId: string | null;
  setCurrentSite: (siteId: string | null) => void;
  setPlan: (plan: PlanSlug) => void;
  setAddons: (addons: string[]) => void;
  toggleAddon: (slug: string) => void;
  setQuestionnaire: (patch: Partial<QuestionnaireState>) => void;
  setJobId: (jobId: string) => void;
  reset: () => void;
}

export const useWizardStore = create<WizardStore>()(
  persist(
    (set) => ({
      plan: null,
      addons: [],
      questionnaire: {},
      jobId: null,
      currentSiteId: null,
      setCurrentSite: (currentSiteId) => set({ currentSiteId }),
      setPlan: (plan) => set({ plan }),
      setAddons: (addons) => set({ addons }),
      toggleAddon: (slug) =>
        set((s) => {
          const current = s.addons ?? [];
          return {
            addons: current.includes(slug)
              ? current.filter((a) => a !== slug)
              : [...current, slug],
          };
        }),
      setQuestionnaire: (patch) =>
        set((s) => ({ questionnaire: { ...s.questionnaire, ...patch } })),
      setJobId: (jobId) => set({ jobId }),
      reset: () => set({ plan: null, addons: [], questionnaire: {}, jobId: null }),
    }),
    {
      name: "rumio-app-wizard",
      // v2 added questionnaire.airbnbListingUrls; v3 adds the top-level addons
      // array. Both migrates carry prior state forward (the fields are
      // additive) so a customer mid-onboarding across a version bump doesn't
      // lose progress — addons just needs a default so callers never see
      // undefined where they expect an array.
      // v4 adds questionnaire.voiceNumberType / voiceAreaCode. Additive and
      // optional, so the v3 migrate carries forward unchanged — a customer
      // mid-onboarding keeps their answers and simply has no voice fields,
      // which is what a customer who bought no voice tier should have.
      // v5 adds currentSiteId (null = server picks), likewise additive.
      version: 5,
      migrate: (persisted) => {
        const prior = persisted as Partial<WizardStore>;
        return {
          ...prior,
          addons: prior.addons ?? [],
          currentSiteId: prior.currentSiteId ?? null,
        } as WizardStore;
      },
    },
  ),
);
